import { useState, useEffect, useCallback } from 'react';
import { api, clearSettingsCache, onSettingsCacheCleared } from '../services/api';
import { useLocation } from '../contexts/LocationContext';

// Module-level cache for instant hydration on mount (prevents flash of defaults)
// This is separate from the API-level cache in api.js. Both must be cleared together.
let settingsCache = {};
let settingsListeners = [];

// When the API-level settings cache is cleared (after saves), also clear our module-level cache.
// This ensures the next component mount doesn't hydrate from stale data.
onSettingsCacheCleared(() => {
  settingsCache = {};
});

// Notify all mounted useSettings instances when data changes
const notifyListeners = (cacheKey, newSettings) => {
  settingsListeners.forEach(listener => listener(cacheKey, newSettings));
};

export function useSettings() {
  const { selectedLocationId } = useLocation();
  const cacheKey = selectedLocationId || 'default';

  // Initialize from module-level cache if available (instant display, no flash of defaults)
  const [settings, setSettings] = useState(settingsCache[cacheKey] || null);
  const [loading, setLoading] = useState(!settingsCache[cacheKey]);

  useEffect(() => {
    // Always fetch on mount or location change to ensure freshness.
    // If the API-level cache is still valid, this returns instantly (no network request).
    // If it was cleared (after a save), this makes a fresh network request.
    fetchSettings();

    // Subscribe to cross-component updates (when another useSettings instance fetches new data)
    const onUpdate = (updatedCacheKey, newSettings) => {
      if (updatedCacheKey === cacheKey) {
        setSettings(newSettings);
      }
    };
    settingsListeners.push(onUpdate);

    return () => {
      settingsListeners = settingsListeners.filter(l => l !== onUpdate);
    };
  }, [selectedLocationId, cacheKey]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await api.getSettings(selectedLocationId);
      // Update both module-level cache and component state
      settingsCache[cacheKey] = data;
      setSettings(data);
      // Notify other mounted components (e.g., TabletView and ChemicalUsagePage both open)
      notifyListeners(cacheKey, data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoized injector settings lookup - avoids recalculation on every render
  // Supports both chip.sensor key format and legacy chemical name format
  const getInjectorSettings = useCallback((bay, chemicalOrInjectorKey) => {
    const bayKey = bay.toLowerCase().replace(' ', '');

    // First, check if this is a chip.sensor key (e.g., "0004.sensor7", "DEMO1.sensor1")
    const isInjectorKey = /^[\w]+\.sensor\d+$/.test(chemicalOrInjectorKey);

    let foundSettings = null;

    if (isInjectorKey) {
      // Direct lookup by injector key
      foundSettings = settings?.[bayKey]?.[chemicalOrInjectorKey] || null;
    } else {
      // Legacy lookup by chemical name
      const hasNumberPrefix = /^\d+\.\s*/.test(chemicalOrInjectorKey);
      const cleanChemical = hasNumberPrefix ?
        chemicalOrInjectorKey.replace(/^\d+\.\s*/, '').toLowerCase() :
        chemicalOrInjectorKey.toLowerCase();

      // Look up settings (they're stored in lowercase)
      foundSettings = settings?.[bayKey]?.[cleanChemical] || null;

      // If not found by chemical name, search through injector keys for matching chemical
      if (!foundSettings && settings?.[bayKey]) {
        const baySettings = settings[bayKey];
        for (const key of Object.keys(baySettings)) {
          const injectorSettings = baySettings[key];
          // Check if this injector's chemical_name matches
          const injectorChemical = injectorSettings?.chemical_name?.replace(/^\d+\.\s*/, '').toLowerCase();
          if (injectorChemical === cleanChemical) {
            foundSettings = injectorSettings;
            break;
          }
        }
      }
    }

    // If we found settings, ensure ramp metrics have absolute values
    if (foundSettings) {
      const result = { ...foundSettings };

      // Ensure ramp metrics exist with ABSOLUTE thresholds
      if (!result.rampUp) {
        result.rampUp = { target: 0.5, mediumVal: 0.1, highVal: 0.2 };
      } else if (!result.rampUp.mediumVal && result.rampUp.mediumPct) {
        result.rampUp.mediumVal = 0.1;
        result.rampUp.highVal = 0.2;
      }

      if (!result.rampDown) {
        result.rampDown = { target: 1.0, mediumVal: 0.1, highVal: 0.2 };
      } else if (!result.rampDown.mediumVal && result.rampDown.mediumPct) {
        result.rampDown.mediumVal = 0.1;
        result.rampDown.highVal = 0.2;
      }

      if (!result.between) {
        result.between = { target: 0, mediumVal: 0.47, highVal: 1.0 };
      } else if (!result.between.mediumVal && result.between.mediumPct) {
        result.between.mediumVal = 0.47;
        result.between.highVal = 1.0;
      }

      return result;
    }

    // Return defaults if not found - percentages for large values, absolute for small
    return {
      volume: { target: 100, mediumPct: 10, highPct: 20 },
      flow: { target: 50, mediumPct: 10, highPct: 20 },
      duration: { target: 30, mediumPct: 10, highPct: 20 },
      rampUp: { target: 0.5, mediumVal: 0.1, highVal: 0.2 },
      rampDown: { target: 1.0, mediumVal: 0.1, highVal: 0.2 },
      between: { target: 0, mediumVal: 0.47, highVal: 1.0 }
    };
  }, [settings]);

  // Memoized color calculation - avoids recalculation on every render
  const getColorForValue = useCallback((bay, chemical, metric, value, statisticalAvg = null) => {
    const injSettings = getInjectorSettings(bay, chemical);
    const metricSettings = injSettings[metric];

    if (!metricSettings || value === null || value === undefined) return '#333';

    // Use statistical average if provided, otherwise fall back to target
    const target = statisticalAvg !== null ? statisticalAvg : metricSettings.target;

    // For rampUp, rampDown, and between - use ABSOLUTE THRESHOLDS from mediumVal/highVal
    if (metric === 'rampUp' || metric === 'rampDown' || metric === 'between') {
      const mediumThreshold = metricSettings.mediumVal || (metric === 'between' ? 0.47 : 0.1);
      const highThreshold = metricSettings.highVal || (metric === 'between' ? 1.0 : 0.2);

      if (metric === 'between') {
        if (Math.abs(target) > 0.01) {
          const diff = Math.abs(value - target);
          if (diff > highThreshold) return '#ff6b6b';
          if (diff > mediumThreshold) return '#ffa500';
          return 'green';
        } else {
          if (value > highThreshold) return '#ff6b6b';
          if (value > mediumThreshold) return '#ffa500';
          return 'green';
        }
      } else {
        const diff = Math.abs(value - target);
        if (diff > highThreshold) return '#ff6b6b';
        if (diff > mediumThreshold) return '#ffa500';
        return 'green';
      }
    }

    // For volume, flow, duration - use PERCENTAGES
    if (target === 0 || Math.abs(target) < 0.01) {
      const absoluteDiff = Math.abs(value - target);
      if (absoluteDiff > 1.0) return '#ff6b6b';
      if (absoluteDiff > 0.5) return '#ffa500';
      return 'green';
    }

    const percentDiff = Math.abs((value - target) / target * 100);

    if (percentDiff > metricSettings.highPct) return '#ff6b6b';
    if (percentDiff > metricSettings.mediumPct) return '#ffa500';
    return 'green';
  }, [getInjectorSettings]);

  const getColorForLeakage = useCallback((value) => {
    if (value > 0.5) return 'red';
    if (value > 0) return 'orange';
    return 'green';
  }, []);

  // Force refresh — clears both API cache and module-level cache, then refetches
  const refreshSettings = () => {
    clearSettingsCache(); // Clears API cache + triggers onSettingsCacheCleared (clears module-level cache)
    fetchSettings();
  };

  const updateSetting = async (bay, chemical, metric, settingsData) => {
    try {
      await api.updateSettings(bay, chemical, {
        [metric]: settingsData,
        locationId: selectedLocationId  // Ensure per-location scoping
      });
      await refreshSettings();
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  const saveBulkSettings = async (updates) => {
    try {
      await api.bulkUpdateSettings(updates, selectedLocationId);
      await refreshSettings();
      return true;
    } catch (error) {
      console.error('Error saving bulk settings:', error);
      throw error;
    }
  };

  return {
    settings,
    loading,
    getInjectorSettings,
    getColorForValue,
    getColorForLeakage,
    refreshSettings,
    updateSetting,
    saveBulkSettings
  };
}
